import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
// ─────────────────────────────────────────────────────────────────────────────
// Feature dialogs from components/
// ─────────────────────────────────────────────────────────────────────────────
// Edit routine dialog
import { EditRoutineDialogComponent } from './edit-routine-dialog/edit-routine-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Routine stats modal
import { RoutineStatsModalComponent } from './routine-stats-modal/routine-stats-modal.component';
// ─────────────────────────────────────────────────────────────────────────────
// Workflow create dialog
import { WorkflowCreateDialogComponent } from './workflow-dropdown/workflow-create-dialog/workflow-create-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Export project dialog
import { ExportProjectDialogComponent } from './export-project-dialog/export-project-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Invite team member dialog
import { InviteTeamMemberDialogComponent } from './invite-team-member-dialog/invite-team-member-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Remove team member dialog
import { RemoveTeamMemberDialogComponent, RemoveTeamMemberDialogData } from './remove-team-member-dialog/remove-team-member-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Edit role dialog
import { EditRoleDialogComponent } from './edit-role-dialog/edit-role-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Add connection dialog
import { AddConnectionDialogComponent } from './add-connection-dialog/add-connection-dialog.component';
// Connection success dialog
import { ConnectionSuccessDialogComponent } from './connection-success-dialog/connection-success-dialog.component';
// Connection testing dialog
import {
  ConnectionTestingDialogComponent,
  ConnectionTestingDialogData,
} from './connection-testing-dialog/connection-testing-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Add plan dialog
import { AddPlanDialogComponent } from './add-plan-dialog/add-plan-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Step form dialog
import { StepFormDialogComponent } from './step-form-dialog/step-form-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Snapshot management modal
import { SnapshotManagementModalComponent } from './snapshot-management-modal/snapshot-management-modal.component';
// ─────────────────────────────────────────────────────────────────────────────
// Secret value dialog
import { SecretValueDialogComponent } from './secrets/secret-value-dialog/secret-value-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Project create dialog
import { ProjectCreateDialogComponent } from './project-create-dialog/project-create-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Import project dialog (components)
import { ImportProjectDialogComponent } from './import-project-dialog/import-project-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Create snapshot modal
import { CreateSnapshotModalComponent } from './create-snapshot-modal/create-snapshot-modal.component';
// ─────────────────────────────────────────────────────────────────────────────
// Edit snapshot modal
import { EditSnapshotModalComponent } from './edit-snapshot-modal/edit-snapshot-modal.component';
// ─────────────────────────────────────────────────────────────────────────────
// View snapshot description modal
import { ViewSnapshotDescriptionModalComponent } from './view-snapshot-description-modal/view-snapshot-description-modal.component';
// ─────────────────────────────────────────────────────────────────────────────
// Confirm apply snapshot dialog
import { ConfirmApplySnapshotDialogComponent } from './confirm-apply-snapshot-dialog/confirm-apply-snapshot-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Save and apply snapshot dialog
import { SaveAndApplySnapshotDialogComponent } from './save-and-apply-snapshot-dialog/save-and-apply-snapshot-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Plan history modal
import { PlanHistoryModalComponent } from './plan-history-modal/plan-history-modal.component';
// Run now dialog
import { RunNowDialogComponent, RunNowDialogData, RunNowDialogResult } from './run-now-dialog/run-now-dialog.component';
// ─────────────────────────────────────────────────────────────────────────────
// Project settings page (used as dialog)
import { ProjectSettingsPageComponent } from '../pages/project-settings-page/project-settings-page.component';
// ─────────────────────────────────────────────────────────────────────────────
// Confirm dialog (components version)
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';

/**
 * FeatureDialogService - Manages feature-level dialogs from components/ directory
 * 
 * This service handles dialogs that belong to the feature layer (components/),
 * fixing the architecture violation where shared services imported feature components.
 * 
 * Architecture:
 * - Shared dialogs (shared/modules/) → DialogService (shared/services/)
 * - Feature dialogs (components/) → FeatureDialogService (components/)
 * - Layout dialogs (layout/) → Can be handled by either service
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureDialogService {
  private readonly dialog = inject(MatDialog);

  editRoutineDialog(data: any): Observable<any> {
    const ref = this.dialog.open(EditRoutineDialogComponent, {
      data,
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'edit-routine-dialog-container',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  routineStatsModal(data: any): Observable<any> {
    const ref = this.dialog.open(RoutineStatsModalComponent, {
      data,
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  workflowCreateDialog(data: any): Observable<any> {
    const ref = this.dialog.open(WorkflowCreateDialogComponent, {
      data,
      width: '500px',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  exportProjectDialog(data?: any): Observable<any> {
    const ref = this.dialog.open(ExportProjectDialogComponent, {
      data,
      width: '500px',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  inviteTeamMemberDialog(data: any): Observable<any> {
    const ref = this.dialog.open(InviteTeamMemberDialogComponent, {
      data,
      width: '540px',
      disableClose: false,
      autoFocus: 'dialog',
    });
    return ref.afterClosed();
  }

  removeTeamMemberDialog(data: RemoveTeamMemberDialogData): Observable<boolean> {
    const ref = this.dialog.open(RemoveTeamMemberDialogComponent, {
      data,
      width: '368px',
      maxWidth: '95vw',
      panelClass: 'remove-team-member-dialog',
      disableClose: false,
      autoFocus: 'dialog',
    });
    return ref.afterClosed();
  }

  editRoleDialog(data: any): Observable<any> {
    const ref = this.dialog.open(EditRoleDialogComponent, {
      data,
      width: '400px',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  addConnectionDialog(data: any): Observable<any> {
    const ref = this.dialog.open(AddConnectionDialogComponent, {
      data,
      width: '700px',
      // Responsive on small windows.
      maxWidth: '95vw',
      // Cap the dialog at the viewport so its body scrolls internally instead of
      // the CDK overlay growing a scrollbar. The overlay scrollbar appearing /
      // disappearing as fields show/hide is what made the centered dialog jump
      // left/right; keeping the dialog within the viewport avoids it.
      maxHeight: '90vh',
      // Top-anchor so height changes grow downward rather than re-centering.
      position: { top: '5vh' },
      disableClose: false,
    });
    return ref.afterClosed();
  }

  connectionSuccessDialog(connectionName: string): Observable<'skip' | 'start-testing'> {
    const ref = this.dialog.open(ConnectionSuccessDialogComponent, {
      data: { connectionName },
      width: '480px',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  connectionTestingDialog(data: ConnectionTestingDialogData): Observable<void> {
    const ref = this.dialog.open(ConnectionTestingDialogComponent, {
      data,
      width: '480px',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  addPlanDialog(data: any): Observable<any> {
    const ref = this.dialog.open(AddPlanDialogComponent, {
      data,
      panelClass: 'add-plan-dialog-panel',
      // Fits General two-column row (320+34+320) + divider + Plan Description without excess dead space
      width: '1080px',
      maxWidth: '95vw',
      height: 'auto',
      maxHeight: '90vh',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  stepFormDialog(data: any): Observable<any> {
    const ref = this.dialog.open(StepFormDialogComponent, {
      data,
      width: '1000px',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  snapshotManagementModal(data: any): Observable<any> {
    const ref = this.dialog.open(SnapshotManagementModalComponent, {
      data,
      width: '700px',
      maxHeight: '80vh',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  secretValueDialog(data: any): Observable<any> {
    const ref = this.dialog.open(SecretValueDialogComponent, {
      data,
      width: '500px',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  projectCreateDialog(data?: any): Observable<any> {
    const ref = this.dialog.open(ProjectCreateDialogComponent, {
      data,
      width: '500px',
      maxWidth: '95vw',
      panelClass: 'project-create-dialog',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  importProjectDialog(data?: any): Observable<any> {
    const ref = this.dialog.open(ImportProjectDialogComponent, {
      data,
      width: '560px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'import-dialog',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  createSnapshotModal(data: any): Observable<any> {
    const ref = this.dialog.open(CreateSnapshotModalComponent, {
      data,
      width: '400px',
      maxHeight: '80vh',
      panelClass: 'create-snapshot-modal',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  editSnapshotModal(data: any): Observable<any> {
    const ref = this.dialog.open(EditSnapshotModalComponent, {
      data,
      width: '600px',
      maxHeight: '80vh',
      panelClass: 'edit-snapshot-modal',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  viewSnapshotDescriptionModal(data: any): Observable<any> {
    const ref = this.dialog.open(ViewSnapshotDescriptionModalComponent, {
      data,
      width: '600px',
      maxWidth: '90vw',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  confirmApplySnapshotDialog(data: any): Observable<any> {
    const ref = this.dialog.open(ConfirmApplySnapshotDialogComponent, {
      data,
      width: '480px',
      maxWidth: '90vw',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  saveAndApplySnapshotDialog(data: any): Observable<any> {
    const ref = this.dialog.open(SaveAndApplySnapshotDialogComponent, {
      data,
      width: '600px',
      maxWidth: '90vw',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  runNowDialog(data: RunNowDialogData): Observable<RunNowDialogResult | undefined> {
    const width = data.variableSchema ? '580px' : '420px';
    const ref = this.dialog.open(RunNowDialogComponent, {
      data,
      width,
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  planHistoryModal(data: any): Observable<any> {
    const ref = this.dialog.open(PlanHistoryModalComponent, {
      data,
      width: '1200px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'plan-history-modal-container',
    });
    return ref.afterClosed();
  }

  projectSettingsDialog(data: any): Observable<any> {
    const ref = this.dialog.open(ProjectSettingsPageComponent, {
      data,
      width: '1280px',
      maxWidth: '90vw',
      // Fixed height (not just maxHeight) so the dialog stays a constant size and
      // does not re-center/jump when switching tabs; tab content scrolls internally.
      height: '90vh',
      maxHeight: '90vh',
      disableClose: false,
    });
    return ref.afterClosed();
  }

  /**
   * Simple confirm dialog using components version
   * For advanced confirm dialogs, use DialogService.confirm() with useLegacySharedDialog option
   */
  confirmDialog(data: {
    title: string;
    message?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    isDestructive?: boolean;
  }): Observable<boolean> {
    const dialogData = {
      title: data.title,
      message: data.message || '',
      confirmButtonText: data.confirmButtonText || 'Confirm',
      cancelButtonText: data.cancelButtonText || 'Cancel',
      isDestructive: data.isDestructive === true,
    };

    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
      width: '400px',
      panelClass: 'confirm-dialog',
      disableClose: true,
    });
    return ref.afterClosed();
  }
}
